import React, { useState, useEffect } from 'react';
import { bibleService } from '../../services/bibleService';
import { GLOSSARY_CATEGORIES, GLOSSARY_DATA } from '../../data/glossaryData';
import type { BiblePassage } from '../../types/bible';
import { db } from '../../lib/db';
import { useNotesStore } from '../../store/notesStore';
import { useNavigate } from 'react-router-dom';

interface BibleStudyViewProps {
  reference: string;
  bibleId?: string;
  onReferenceChange?: (reference: string) => void;
}

const BibleStudyView = ({
  reference,
  bibleId = 'de4e12af7f28f599-02',
  onReferenceChange,
}: BibleStudyViewProps) => {
  const [passage, setPassage] = useState<BiblePassage | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const { openNoteModal } = useNotesStore();
  const navigate = useNavigate();

  useEffect(() => {
    loadPassage();
  }, [reference, bibleId]);

  const loadPassage = async () => {
    try {
      const passageData = await bibleService.getPassage(bibleId, reference);
      setPassage(passageData);
    } catch (error) {
      console.error('Error loading passage:', error);
    }
  };

  const handleReferenceClick = (newRef: string) => {
    // Parse the reference (e.g., "John 3:16" -> { book: "John", chapter: 3, verse: 16 })
    const parts = newRef.split(' ');
    const book = parts.slice(0, parts.length - 1).join(' '); // Handles multi-word book names
    const [chapter, verse] = parts[parts.length - 1].split(':').map(Number);
    
    // Format book name for URL (e.g., "1 John" -> "1-john")
    const bookForUrl = book.toLowerCase().replace(/ /g, '-');
    
    // Navigate to the Bible reader with the correct verse
    navigate(`/read/kjv/${bookForUrl}/${chapter}`);
    
    // Update the reference in the parent component
    onReferenceChange?.(newRef);
  };

  const handleSaveNote = (verse: string) => {
    const [book, chapterVerse] = reference.split(' ');
    const [chapter, verseNum] = chapterVerse.split(':').map(Number);
    
    openNoteModal({
      book,
      chapter,
      verse: verseNum || 0,
      text: verse,
      version: bibleId
    });
  };

  const filterGlossaryEntries = () => {
    return GLOSSARY_DATA.filter(entry => {
      const matchesSearch = entry.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          entry.definition.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !selectedCategory || entry.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  };

  return (
    <div className="bg-obsidian-900 text-white min-h-screen">
      <div className="max-w-7xl mx-auto p-2 sm:p-4 md:p-6">
        <h2 className="text-xl md:text-2xl font-semibold mb-6">Glossary</h2>

        {/* Glossary Content */}
        <div className="space-y-6">
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Bar */}
            <div className="relative flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search glossary..."
                className="w-full bg-obsidian-800 border border-gold-500/20 rounded-lg px-3 md:px-4 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-gold-500"
              />
            </div>
            
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full sm:w-auto bg-obsidian-800 border border-gold-500/20 rounded-lg px-3 md:px-4 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-gold-500"
            >
              <option value="">All Categories</option>
              {GLOSSARY_CATEGORIES.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          {/* Glossary Entries */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
            {filterGlossaryEntries().map((entry) => (
              <div
                key={entry.term}
                className="bg-obsidian-800 border border-gold-500/20 rounded-lg p-4 md:p-6"
              >
                <h3 className="text-lg md:text-xl font-semibold text-gold-500 mb-2">
                  {entry.term}
                </h3>
                <p className="text-sm md:text-base text-gray-300 mb-3 md:mb-4">{entry.definition}</p>
                <div className="space-y-2">
                  <p className="text-xs md:text-sm text-gray-400">
                    Category: {entry.category}
                  </p>
                  {entry.references && entry.references.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs md:text-sm text-gray-400 mb-2">Key Verses:</p>
                      <div className="flex flex-wrap gap-2">
                        {entry.references.map((ref) => (
                          <button
                            key={ref}
                            onClick={() => handleReferenceClick(ref)}
                            className="inline-flex items-center px-2.5 py-1.5 bg-obsidian-700/50 hover:bg-obsidian-600/50 rounded-full text-xs md:text-sm text-gold-500 hover:text-gold-400 transition-colors"
                          >
                            {ref}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {entry.relatedTerms.length > 0 && (
                    <div>
                      <p className="text-xs md:text-sm text-gray-400">Related Terms:</p>
                      <div className="flex flex-wrap gap-1.5 md:gap-2 mt-1">
                        {entry.relatedTerms.map((term) => (
                          <span
                            key={term}
                            className="inline-block px-2 py-0.5 md:py-1 bg-obsidian-700 rounded-full text-xs text-gray-300"
                          >
                            {term}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export { BibleStudyView };
